import React, { useState } from 'react';
import Modal from './Modal';
import ImagePreview from './ImagePreview';

interface ItemDetailsProps {
  item: {
    _id: string;
    name: string;
    description: string;
    category: string;
    location: string;
    condition: string;
    owner: {
      firstName: string;
      lastName: string;
    };
    status: string;
    images?: Array<string | { url: string; caption?: string }>;
  };
  categories: Array<{
    _id: string;
    name: string;
  }>;
  isOpen: boolean;
  onClose: () => void;
}

const ItemDetails: React.FC<ItemDetailsProps> = ({ item, categories, isOpen, onClose }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);

  const getImageUrl = (image: string | { url: string; caption?: string } | undefined) => {
    if (!image) {
      return '';
    }

    // Handle both string URLs and object URLs
    const url = typeof image === 'string' ? image : image.url;
    
    if (!url) {
      return '';
    }

    // If the URL is already a full URL (starts with http), return it as is
    if (url.startsWith('http')) {
      return url;
    }
    
    // Otherwise, it's a local URL, so prepend the uploads URL
    const baseUrl = import.meta.env.VITE_UPLOADS_URL || 'http://localhost:3001';
    return `${baseUrl}${url}`;
  };

  // Process images to ensure they all have the correct format
  const processedImages = item.images?.map(image => {
    if (typeof image === 'string') {
      return { url: getImageUrl(image) };
    }
    return { ...image, url: getImageUrl(image) };
  }) || [];

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(cat => cat._id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Item Details"
        size="lg"
        actions={{
          secondary: {
            label: 'Close',
            onClick: onClose
          }
        }}
      >
        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{item.name}</h3>
              <p className="mt-1 text-sm text-gray-500">{item.description}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm">
                <span className="font-medium">Category:</span> {getCategoryName(item.category)}
              </p>
              <p className="text-sm">
                <span className="font-medium">Location:</span> {item.location}
              </p>
              <p className="text-sm">
                <span className="font-medium">Condition:</span> {item.condition}
              </p>
              <p className="text-sm">
                <span className="font-medium">Status:</span>{' '}
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                  ${item.status === 'available' ? 'bg-green-100 text-green-800' :
                    item.status === 'borrowed' ? 'bg-blue-100 text-blue-800' :
                    item.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                    item.status === 'lost' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                  {item.status}
                </span>
              </p>
              <p className="text-sm">
                <span className="font-medium">Owner:</span> {item.owner.firstName} {item.owner.lastName}
              </p>
            </div>
          </div>

          {processedImages.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Images</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {processedImages.map((image, index) => (
                  <div 
                    key={index} 
                    className="relative aspect-w-3 aspect-h-2 cursor-pointer group"
                    onClick={() => setSelectedImageIndex(index)}
                  >
                    <img
                      src={image.url}
                      alt={`${item.name} - ${index + 1}`}
                      className="object-cover rounded-lg shadow-sm transition-opacity group-hover:opacity-90"
                    />
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-sm">
                        Click to view
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {selectedImageIndex !== null && (
        <ImagePreview
          images={processedImages}
          initialImageIndex={selectedImageIndex}
          isOpen={selectedImageIndex !== null}
          onClose={() => setSelectedImageIndex(null)}
        />
      )}
    </>
  );
};

export default ItemDetails; 